import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminAutomatedEmailsQuery } from '../../../features/admin/hooks/use-admin-api'

const EMAIL_TYPE_LABELS: Record<string, string> = {
  founder_onboarding_incomplete: 'Founder: Finish Setup',
  founder_welcome_no_deck: 'Founder: Welcome (No Evaluation)',
  founder_evaluation_ready: 'Founder: Evaluation Ready',
  founder_dataroom_not_shared: 'Founder: Profile Not Shared',
  founder_dataroom_viewed: 'Founder: Profile Viewed',
  founder_stall_no_evaluation: 'Founder: 2-Week Stall (No Evaluation)',
  founder_stall_not_shared: 'Founder: 2-Week Stall (Not Shared)',
  investor_welcome: 'Investor: Welcome',
  investor_onboarding_incomplete: 'Investor: Onboarding Incomplete',
  investor_observer_alert: 'Investor: Follow Alert',
  investor_watchdog_matches: 'Investor: Watchdog Matches',
  investor_invite_joined: 'Investor: Joined Through Invite',
  investor_chatgpt_listed: 'Investor: ChatGPT Listed',
}

type SearchParams = { page?: number; search?: string; emailType?: string; status?: string }
const LIMIT = 20

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive'> = {
  SENT: 'success',
  PENDING: 'warning',
  FAILED: 'destructive',
}

function formatStatus(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase()
}

export const Route = createFileRoute('/admin/emails/')({
  component: Page,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
    emailType: (search.emailType as string) || undefined,
    status: (search.status as string) || undefined,
  }),
})

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatEmailType(emailType: string): string {
  return (
    EMAIL_TYPE_LABELS[emailType] ??
    emailType
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  )
}

type EmailItem = {
  id: string
  emailType: string
  subject: string
  recipientEmail: string
  status: string
  createdAt: string | Date
  externalId: string | null
  user: {
    id: string
    name: string | null
    email: string
  }
}

const columns: TableColumn<EmailItem>[] = [
  {
    header: 'ID',
    width: '80px',
    render: (email) => (
      <Link
        to="/admin/emails/$id"
        params={{ id: email.id }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {email.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    header: 'Type',
    render: (email) => (
      <Badge variant="secondary" size="sm">
        {formatEmailType(email.emailType)}
      </Badge>
    ),
  },
  {
    header: 'Recipient',
    render: (email) => (
      <Link
        to="/admin/users/$id"
        params={{ id: email.user.id }}
        className="text-primary hover:underline"
      >
        <CellText>{email.recipientEmail}</CellText>
      </Link>
    ),
  },
  {
    header: 'Status',
    width: '110px',
    render: (email) => (
      <Badge variant={STATUS_VARIANT[email.status] ?? 'secondary'} size="sm">
        {formatStatus(email.status)}
      </Badge>
    ),
  },
  {
    header: 'Created',
    width: '200px',
    render: (email) => <CellText>{formatDate(email.createdAt)}</CellText>,
  },
]

function Page() {
  const { page = 1, search, emailType, status } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const emailsQuery = useAdminAutomatedEmailsQuery({
    page,
    limit: LIMIT,
    search: search || undefined,
    emailType: emailType || undefined,
    status: status || undefined,
  })

  const emails = emailsQuery.data?.data?.items ?? []
  const total = emailsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-3">
        <SearchInput
          placeholder="Search emails..."
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() =>
            navigate({ search: { page: 1, search: searchInput || undefined, emailType, status } })
          }
        />
        <select
          value={emailType ?? ''}
          onChange={(e) =>
            navigate({
              search: { page: 1, search, emailType: e.target.value || undefined, status },
            })
          }
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All types</option>
          {Object.entries(EMAIL_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={status ?? ''}
          onChange={(e) =>
            navigate({
              search: { page: 1, search, emailType, status: e.target.value || undefined },
            })
          }
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="SENT">Sent</option>
          <option value="FAILED">Failed</option>
        </select>
      </div>
      <DataTable
        columns={columns}
        data={emails}
        isLoading={emailsQuery.isLoading}
        emptyMessage="No automated emails found."
        keyExtractor={(e) => e.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search, emailType, status } })}
      />
    </div>
  )
}
